import { Chart } from "react-google-charts";
import {
	DATA_TYPES,
	FONTS,
	PRIMARY_COLOR,
	SECONDARY_COLOR,
	THIRD_COLOR,
} from "../../constants";
import { stateType } from "../../state";

import { createStyles, makeStyles, Theme } from "@material-ui/core";
import { calcMax, calcMin, isNaN2 } from "../../helpers/DataHelpers";
import { formatNumber } from "../../helpers/FormatNumber";
import { Scatter } from "./Scatter";

export const BAR_COLORS = [PRIMARY_COLOR, SECONDARY_COLOR];
export const MAX_SCATTER_POINTS = 2000;

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		chartContainer: {
			width: "90%",
			height: "60%",
			marginLeft: "5%",
		},
		chartContainer2: {
			width: "100%",
			height: "100%",
			display: "flex",
			flexDirection: "column",
			position: "relative",
		},
		legendContainer: {
			display: "flex",
			flexDirection: "row",
			marginTop: "-280px",
			marginLeft: "20px",
			width: "100%",
			justifyContent: "center",
			marginBottom: "-10px",
			zIndex: 100,
		},
		legendItemContainer: {
			display: "flex",
			flexDirection: "row",
			zIndex: 100,
		},
		ellipse: {
			fontSize: "15px",
			fontFamily: FONTS,
			marginLeft: "15px",
			marginRight: "5px",
			lineHeight: "22px",
			fontWeight: "bold",
			color: "rgba(0,0,0,.6)",
		},
		sliderContainer: {
			width: "100%",
			justifyContent: "center",
			alignItems: "center",
		},
		titleCorContainer: {
			width: "100%",
			display: "flex",
			flexDirection: "column",
			justifyContent: "spaceBetween",
			marginBottom: "10px",
			marginTop: "5px",
		},
	})
);

type chartInputType = { state: stateType; xData: number[]; yData: number[] };
export function ScatterChart({ state, xData, yData }: chartInputType) {
	const classes = useStyles();

	let ticks = [];
	let minX = 0;
	let maxX = 1;

	ticks = null;
	let val = xData.filter((val) => !isNaN2(val));
	minX = calcMin(val, DATA_TYPES.number) * 0.99;
	maxX = calcMax(val, DATA_TYPES.number) * 1.01;

	let val2 = yData.filter((val) => !isNaN2(val));
	let minY = calcMin(val2, DATA_TYPES.number) * 0.99;
	let maxY = calcMax(val2, DATA_TYPES.number) * 1.01;

	let data: any[][] = [
		[
			{ type: "number" },
			{ type: "number" },
			{ role: "style" },
			{ role: "tooltip" },
		],
	];

	var sett = `fill-color: ${THIRD_COLOR}; fill-opacity: 0.6; stroke-color: #76A7FA; stroke-width: 0;`;

	for (let i = 0; i < xData.length; i++) {
		data.push([
			xData[i],
			yData[i],
			sett,
			"Actual: " +
				xData[i] +
				"\n" +
				"Predicted: " +
				formatNumber(yData[i]),
		]);
	}
	let scatterType = true;
	return (
		<div
			style={{
				marginTop: scatterType ? "-10px" : "40px",
				height: scatterType ? "380px" : "450px",
			}}
		>
			<Scatter
				data={data}
				ticks={ticks}
				minX={minX}
				maxX={maxX}
				minY={minY}
				maxY={maxY}
				xTitle={"Actual Values"}
				yTitle={"Predicted Values"}
				height={"600px"}
			></Scatter>
		</div>
	);
}

export function BarChart({ state, xData, yData }: chartInputType) {
	const header: any[] = [""];
	for (let val of ["Predicted", "Acutal"]) {
		header.push(val);
		header.push({ role: "style" });
		header.push({ role: "tooltip" });
		header.push({ role: "annotation" });
	}

	let col1 = `fill-color: ${PRIMARY_COLOR}; fill-opacity: ${0.9}; stroke-color: #76A7FA; stroke-width: 0;`;
	let col2 = `fill-color: ${SECONDARY_COLOR}; fill-opacity: ${0.9}; stroke-color: #76A7FA; stroke-width: 0;`;

	let preds = state.state.saveState.modelTraining.catModelPredictions;
	let classes = Array.from(Object.keys(preds)).sort();
	let data = [header];
	for (let cl of classes) {
		let actualCount = preds[cl].actualCount;
		let predCount = preds[cl].predictedCount;
		data.push([
			cl,
			actualCount,
			col1,
			"Actual",
			actualCount,
			predCount,
			col2,
			"Predicted",
			predCount,
		]);
	}
	for (let i = 1; i < data.length; i++) {
		data[i][0] =
			data[i][0] +
			"\n" +
			" (" +
			formatNumber(preds[data[i][0]].accuracy) +
			"%" +
			")";
	}

	return (
		<Chart
			style={{
				height: "500px",
				width: "98%",
			}}
			chartType="ColumnChart"
			loader={<div>Loading Chart</div>}
			data={data}
			options={{
				// Material design options
				colors: BAR_COLORS,
				legend: { position: "none" },
				chartArea: {
					width: "90%",
					height: "100%",
					top: 30,
					bottom: 80,
					left: 80,
				},

				vAxis: {
					minorGridlines: { color: "white" },
					gridlines: { color: "rgb(220,220,220)" },
					baselineColor: "rgb(220,220,220)",
					textStyle: { fontName: FONTS },
					title: `Counts`,
				},
				hAxis: {
					minorGridlines: { color: "white" },
					baselineColor: "rgb(220,220,220)",
					gridlines: { color: "rgb(220,220,220)" },
					textStyle: { fontName: FONTS },
					title: "Predicted Outputs (Accuracy %)",
				},

				annotations: {
					alwaysOutside: true,
					textStyle: {
						color: PRIMARY_COLOR,
						fontSize: 14,
						fontFamily: FONTS,
					},
				},
			}}
		/>
	);
}
